
import React, { useMemo } from 'react';
import { Card } from '@dhis2/ui';
import MetricCard from './MetricCard';
import { GiNotebook } from 'react-icons/gi';

// ProgressBar component
const ProgressBar = ({ value, height = '6px', borderRadius = '3px' }) => (
  <div style={{
    width: '100%',
    height,
    backgroundColor: '#e5e7eb',
    borderRadius
  }}>
    <div style={{
      width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
      height: '100%',
      backgroundColor: getProgressColor(value || 0),
      borderRadius,
      transition: 'width 0.3s ease'
    }} />
  </div>
);

const getProgressColor = (progress) => {
  if (progress >= 80) return '#28a745';
  if (progress >= 50) return '#ffc107';
  return '#dc3545';
};

// Helper to extract data value from task
const getDataValue = (dataValues = [], dataElement) =>
  dataValues.find(dv => dv.dataElement === dataElement)?.value || '';

// Helper to check if task is in fiscal year
const isTaskInFiscalYear = (task, fiscalYear) => {
  if (!fiscalYear?.start || !fiscalYear?.end) return true;

  const fyStart = new Date(fiscalYear.start);
  const fyEnd = new Date(fiscalYear.end);
  
  const dataValues = task.dataValues || [];
  const taskWeek = getDataValue(dataValues, 'EiL3lPaL908');
  const eventDate = task.eventDate;
  
  const taskDate = taskWeek || eventDate;
  if (!taskDate) return true;
  
  const date = new Date(taskDate);
  return date >= fyStart && date <= fyEnd;
};

const TaskMetrics = ({ 
  orgUnitId, 
  performanceData, 
  accessibleOrgUnitIds, 
  filterScope 
}) => {
  // Get tasks from performanceData (already filtered by fiscal year from parent)
  const tasks = performanceData?.tasks?.events || [];

  // Calculate metrics from tasks
  const metrics = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        overdueTasks: 0,
        inProgressTasks: 0,
        notStartedTasks: 0,
        completionRate: 0,
        averageProgress: 0,
        highPriorityTasks: 0
      };
    }

    const now = new Date();
    let completedTasks = 0;
    let overdueTasks = 0;
    let inProgressTasks = 0;
    let notStartedTasks = 0;
    let highPriorityTasks = 0;
    let totalProgress = 0;
    let tasksWithProgress = 0;

    tasks.forEach(task => {
      const dataValues = task.dataValues || [];
      const progress = parseInt(getDataValue(dataValues, 'AElpNxzTdRh')) || 0;
      const status = getDataValue(dataValues, 'LuTwfuN34pc') || 'not_started';
      const dueDate = getDataValue(dataValues, 'IY0xyOmlEr7');
      
      // Count by status
      if (progress === 100 || status === 'completed') {
        completedTasks++;
      } else if (progress > 0 && progress < 100) {
        inProgressTasks++;
      } else {
        notStartedTasks++;
      }
      
      // Count overdue tasks
      if (dueDate && new Date(dueDate) < now && progress < 100) {
        overdueTasks++;
      }
      
      // Count high priority (progress < 50%)
      if (progress < 50 && progress > 0) {
        highPriorityTasks++;
      }
      
      // Calculate average progress
      if (progress > 0) {
        totalProgress += progress;
        tasksWithProgress++;
      }
    });

    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const averageProgress = tasksWithProgress > 0 ? Math.round(totalProgress / tasksWithProgress) : 0;

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      inProgressTasks,
      notStartedTasks,
      completionRate,
      averageProgress,
      highPriorityTasks
    };
  }, [tasks]);

  // Show message if no tasks
  if (tasks.length === 0) {
    return (
      <Card style={{ padding: 16, marginBottom: 24 }}>
        <h4 style={{ marginBottom: 16 }}>Touch Points Management Overview</h4>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: '#6c757d'
        }}>
          <p style={{ fontSize: 16, marginBottom: 8 }}> <GiNotebook size={18} /> No Touch Points</p>
          <p style={{ fontSize: 14 }}>There are no touch points assigned for the current period.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      <h4 style={{ marginBottom: 16 }}>Touch Points Management Overview</h4>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16,
        marginBottom: 16
      }}>
        <MetricCard 
          title="Total Touch Points" 
          value={metrics.totalTasks} 
          color="#007bff"
        />
        <MetricCard 
          title="Completed" 
          value={metrics.completedTasks} 
          subtitle={`${metrics.completionRate}% completion`}
          color="#28a745"
        />
        <MetricCard 
          title="In Progress" 
          value={metrics.inProgressTasks} 
          color="#ffc107"
        />
        <MetricCard 
          title="Overdue" 
          value={metrics.overdueTasks} 
          color="#dc3545"
        />
        <MetricCard 
          title="Not Started" 
          value={metrics.notStartedTasks} 
          color="#6c757d"
        />
        <MetricCard 
          title="High Priority" 
          value={metrics.highPriorityTasks} 
          color="#fd7e14"
        />
      </div>

      {/* Progress bar for overall completion */}
      {metrics.totalTasks > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: 8,
            fontSize: 14 
          }}>
            <span>Overall Progress</span>
            <span>{metrics.averageProgress}%</span>
          </div>
          <ProgressBar value={metrics.averageProgress} />
        </div>
      )}
    </Card>
  );
};

export default TaskMetrics;